import { formDataToObject, ICommonDataGrid, IIdName, ISharedConfig, setSnackbar } from '@shared/library';
import axios, { AxiosError } from 'axios';
import moment from 'moment';
import React from "react";
import * as XLSX from 'xlsx';
import config from '../../../../../../../../config';
import "./InterventionExport.scss";
//#region ICONS
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded';
//#endregion
//#region COMPONENTS
import GestDataGrid from "@shared/library/dist/components/gest/GestDataGrid/GestDataGrid";
import GestToggleButtonGroup from '@shared/library/dist/components/gest/GestInputs/button/group/GestLabelButtonGroup';
import GestDatePicker from '@shared/library/dist/components/gest/GestInputs/pickers/date/GestDatePicker';
import GestSelect from "@shared/library/dist/components/gest/GestInputs/select/GestSelect";
import GestTextArea from "@shared/library/dist/components/gest/GestInputs/text area/GestTextArea";
import { CustomDetails } from "../../../../../../../gest";
//#endregion
//MODELS
import { IGestSelectOption } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';

interface IExportQuery extends IIdName {
    parameters: string[];
}

const InterventionExport = (props: { lang: any; config: ISharedConfig; }) => {
    const exportFormats: IGestSelectOption<undefined>[] = [
        {
            value: 'csv',
            label: 'CSV (.csv)'
        },
        {
            value: 'xlsx',
            label: 'Excel (.xlsx)'
        }
    ];
    const csvSeparators: IGestSelectOption<undefined>[] = [
        {
            value: ';',
            label: ';'
        },
        {
            value: ',',
            label: ','
        },
        {
            value: '\t',
            label: 'Tab'
        }
    ];

    //#region STATES
    //FORM
    const [exports, setExports] = React.useState<IGestSelectOption<string[]>[]>([]);
    const [exportFormat, setExportFormat] = React.useState(exportFormats[0]);
    const [separator, setSeparator] = React.useState(csvSeparators[0]);
    const [selectedExport, setSelectedExport] = React.useState<IGestSelectOption<string[]>>();
    const form = React.useRef<HTMLFormElement>(null);

    //PREVIEW
    const [exportPreview, setExportPreview] = React.useState<ICommonDataGrid<any>>();

    //FLAGS
    const [exportLoading, setExportLoading] = React.useState(false);
    //#endregion

    //#region METHODS
    //#region API CALLS
    const getExports = () => {
        setExportLoading(true);
        axios.get<IExportQuery[]>(`${config.api.internal}/WFM/Interventions/Exports`, { withCredentials: true })
            .then(res => {
                const options = res.data.map<IGestSelectOption<string[]>>(v => ({
                    value: v.id,
                    label: v.name,
                    data: v.parameters
                }));
                setExports(options);
            })
            .catch((err: AxiosError) => console.error(err))
            .finally(() => setExportLoading(false));
    };

    const getExportPreview = (event?: React.SubmitEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (selectedExport?.value) {
            const params = form.current ? formDataToObject<any>(form.current) : undefined;

            setExportLoading(true);
            return axios.get<ICommonDataGrid<any>>(`${config.api.internal}/WFM/Interventions/Export/${selectedExport.value}/Preview`, { withCredentials: true, params })
                .then(res => {
                    setExportPreview(res.data);
                    return res.data;
                })
                .catch((err: AxiosError) => {
                    console.error(err);
                    return undefined;
                })
                .finally(() => setExportLoading(false));
        }
    };
    //#endregion

    const downloadExport = () => {
        if (exportPreview) {
            if (exportPreview.InitRows?.length) {
                //remove id from export file
                const rows = exportPreview.InitRows.map(r => {
                    delete r.id;
                    return r;
                });
                //compose filename
                let filename = exports.find(v => v.id === selectedExport?.value)?.name ?? "";
                filename = `WFM EXPORT ${filename} ${moment().format("DD-MM-YYYY HH:mm:ss")}`;
                const ws = XLSX.utils.json_to_sheet(rows, { header: exportPreview.Columns?.map(v => v.field) });

                let blob: Blob;

                switch (exportFormat.value) {
                    case 'xlsx': {
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, "WFM");
                        const xlsxbuff = XLSX.write(wb, { bookType: "xlsx", type: "array" });
                        blob = new Blob([xlsxbuff], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", });
                        break;
                    }

                    default:
                        const csv = XLSX.utils.sheet_to_csv(ws, { FS: separator.value as string });
                        blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                        break;
                }

                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = filename;
                a.click();
                URL.revokeObjectURL(a.href);
            }
            else {
                setSnackbar(props.config, !exportPreview.InitRows ? { message: "Errore durante richiesta risultati esportazione", severity: 'error' } : { message: "Esportazione vuota. Riprova variando i parametri", severity: 'warning' });
            }
        }
        else {
            getExportPreview()?.then(downloadExport);
        };
    };
    //#endregion

    //STATES CHANGE
    React.useEffect(getExports, []);

    return <div id="interventionExport">
        <header>
            <GestSelect
                required
                label={props.lang.select_exportation}
                value={selectedExport?.value}
                options={exports}
                onchange={selected => {
                    setSelectedExport(selected[0]);
                    setExportPreview(undefined);
                }}
                disabled={exportLoading}
                w={250}
            />

            <GestSelect
                required
                tooltip={!selectedExport ? props.lang.select_exportation : undefined}
                disabled={!selectedExport}
                label={props.lang.format}
                value={exportFormat.value}
                options={exportFormats}
                onchange={selected => setExportFormat(selected[0])}
            />

            {exportFormat.value === 'csv' &&
                <GestSelect
                    required
                    tooltip={!selectedExport ? props.lang.select_exportation : undefined}
                    disabled={!selectedExport}
                    label={props.lang.separator}
                    value={separator.value}
                    options={csvSeparators}
                    onchange={selected => setSeparator(selected[0])}
                />}
        </header>

        {selectedExport?.data?.length ?
            <CustomDetails
                variant="add-on"
                isOpen={Boolean(selectedExport?.data?.length)}
                title={props.lang.parameters}
            >
                <form ref={form} onSubmit={getExportPreview}>
                    {selectedExport.data.map(p => {
                        const normalized = p.replaceAll('@', '');
                        const paramName = props.lang[normalized] ?? normalized;

                        return p.toLowerCase().includes('_date')
                            ? <GestDatePicker label={paramName} name={normalized} w={130} noFuture />
                            : <GestTextArea label={paramName} name={normalized} />;
                    })}
                </form>
            </CustomDetails>
            : <></>}

        <GestDataGrid
            lang={props.lang}
            columns={exportPreview?.Columns ?? []}
            rows={exportPreview?.InitRows}
            loading={exportLoading}
            w={"75vw"}
        />

        <GestToggleButtonGroup
            btnw={200}
            btnh={40}
            alignment='right'
            buttons={[
                {
                    tooltip: !selectedExport ? props.lang.select_exportation : undefined,
                    label: props.lang.preview,
                    icon: <PreviewRoundedIcon />,
                    onclick: getExportPreview,
                    loading: exportLoading,
                    disabled: !selectedExport,
                },
                {
                    tooltip: !selectedExport ? props.lang.select_exportation : !exportPreview?.InitRows?.length ? "Nessun risultato da esportare" : undefined,
                    label: "Download",
                    icon: <DownloadRoundedIcon />,
                    onclick: downloadExport,
                    disabled: !selectedExport,
                },
            ]}
        />
    </div>;
};
export default React.memo(InterventionExport);
